<script setup lang="ts">
defineOptions({
  ssr: true,
  client: false,
})
definePageMeta({
  auth: false,
})

const pricingTiers = [
  {
    name: 'Free',
    price: '0',
    features: [
      'Basic matching with verified users',
      'Up to 5 matches per month',
      'Standard safety features',
      'Background check verification',
      'Basic gym filtering',
      'In-app messaging',
    ],
    cta: 'Get Started',
    highlighted: false,
  },
  {
    name: 'Premium',
    price: '19',
    features: [
      'All Free features',
      'Unlimited matches',
      'Advanced matching filters',
      'Priority in matching algorithm',
      'See who viewed your profile',
      'Advanced schedule compatibility',
      'Gym class booking integration',
      'Priority customer support',
    ],
    cta: 'Start Premium Trial',
    highlighted: true,
  },
  {
    name: 'Gym Partnership',
    price: 'Custom',
    features: [
      'All Premium features',
      'Gym-branded profiles',
      'Class schedule integration',
      'Member verification tools',
      'Analytics dashboard',
      'Dedicated account manager',
      'Custom matching preferences',
    ],
    cta: 'Contact Sales',
    highlighted: false,
  },
]

const appName = 'GymBuddy'

// ...existing code...
</script>

<template>
  <div class="min-h-screen bg-gray-50 flex flex-col" no-client>
    <TopToolbar />

    <header class="bg-white shadow-sm">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="py-12 sm:py-16 lg:py-20 text-center">
          <h1 class="text-4xl font-extrabold text-gray-900 sm:text-5xl lg:text-6xl">
            Find Your Perfect Workout Partner
          </h1>
          <p class="mt-6 text-xl text-gray-600 max-w-3xl mx-auto">
            Choose the plan that fits your fitness journey. All plans include verified background checks and safety features.
          </p>
        </div>
      </div>
    </header>

    <main class="flex-1 w-full">
      <div class="max-w-7xl mx-auto py-8 sm:py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8" style="min-height: 600px">
          <div
            v-for="tier in pricingTiers"
            :key="tier.name"
            class="pricing-card bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-200 hover:shadow-xl"
            :class="{ 'ring-2 ring-black ring-offset-2': tier.highlighted }">
            <div class="p-6 sm:p-8 h-full flex flex-col">
              <div>
                <h2 class="text-2xl font-bold text-gray-900">{{ tier.name }}</h2>
                <p class="mt-4 sm:mt-6">
                  <span class="text-4xl sm:text-5xl font-extrabold tracking-tight"
                    >${{ tier.price }}</span
                  >
                  <span
                    v-if="tier.price !== 'Custom'"
                    class="text-lg sm:text-xl font-medium text-gray-500"
                    >/month</span
                  >
                </p>
                <ul class="mt-6 sm:mt-8 space-y-4">
                  <li
                    v-for="feature in tier.features"
                    :key="feature"
                    class="flex items-start space-x-3">
                    <Icon name="lucide:check" class="h-5 w-5 text-green-500 flex-shrink-0 mt-1" />
                    <span class="text-gray-600">{{ feature }}</span>
                  </li>
                </ul>
              </div>
              <div class="mt-auto pt-8">
                <UButton :color="tier.highlighted ? 'black' : 'gray'" class="w-full" size="lg">
                  {{ tier.cta }}
                </UButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <footer class="bg-gray-800 mt-auto">
      <div class="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div class="text-white">
            <div class="flex items-center">
              <Icon name="lucide:dumbbell" class="h-8 w-8" />
              <span class="ml-2 text-xl font-bold">{{ appName }}</span>
            </div>
            <p class="mt-4 text-gray-400 text-sm">
              Connect with safe, verified workout partners at your gym. Build accountability and achieve your fitness goals together.
            </p>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<style scoped>
.pricing-card {
  transform: translateY(0);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  width: 100%; /* Ensure cards take full width of their grid cell */
}

/* Remove min-height from media query */
@media (max-width: 768px) {
  .pricing-card {
    margin-bottom: 1rem;
  }

  .pricing-card:last-child {
    margin-bottom: 0;
  }
}

@media (max-width: 1023px) {
  .pricing-card {
    max-width: 480px; /* Limit card width on smaller screens */
    margin-left: auto;
    margin-right: auto;
  }
}
</style>
